import React from "react";
import { Link, useParams } from "react-router-dom";
import ShopNav from "@/components/ShopNav";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/i18n/LanguageContext";
import Seo, { breadcrumbLd, getSiteUrl } from "@/components/Seo";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";
import { AlertTriangle } from "lucide-react";

const LAST_UPDATE = "2026-02-22";
const SITE_URL = getSiteUrl();

function buildContent(t, kind) {
  const holder = { h: t("legal.sections.holder"), p: t("legal.sections.holder_body") };
  const switchContent = (arr) => ({ blocks: [holder, ...arr] });

  switch (kind) {
    case "aviso":
      return switchContent([
        { h: t("legal.sections.aviso_purpose_title"), p: t("legal.sections.aviso_purpose") },
        { h: t("legal.sections.aviso_ip_title"), p: t("legal.sections.aviso_ip") },
        { h: t("legal.sections.aviso_liability_title"), p: t("legal.sections.aviso_liability") },
        { h: t("legal.sections.aviso_jurisdiction_title"), p: t("legal.sections.aviso_jurisdiction") },
      ]);
    case "privacidad":
      return switchContent([
        { h: t("legal.sections.privacy_intro_title"), p: t("legal.sections.privacy_intro") },
        { h: t("legal.sections.privacy_purpose_title"), p: t("legal.sections.privacy_purpose") },
        { h: t("legal.sections.privacy_basis_title"), p: t("legal.sections.privacy_basis") },
        { h: t("legal.sections.privacy_share_title"), p: t("legal.sections.privacy_share") },
        { h: t("legal.sections.privacy_retention_title"), p: t("legal.sections.privacy_retention") },
        { h: "Derechos · Rights", p: t("legal.sections.privacy_rights") },
      ]);
    case "envios":
      return switchContent([
        { h: t("legal.sections.shipping_scope_title"), p: t("legal.sections.shipping_scope") },
        { h: t("legal.sections.shipping_cost_title"), p: t("legal.sections.shipping_cost") },
        { h: t("legal.sections.shipping_package_title"), p: t("legal.sections.shipping_package") },
        { h: t("legal.sections.shipping_damage_title"), p: t("legal.sections.shipping_damage") },
      ]);
    case "devoluciones":
      return switchContent([
        { h: t("legal.sections.returns_right_title"), p: t("legal.sections.returns_right") },
        { h: t("legal.sections.returns_process_title"), p: t("legal.sections.returns_process") },
        { h: t("legal.sections.returns_refund_title"), p: t("legal.sections.returns_refund") },
      ]);
    case "cookies":
      return switchContent([
        { h: t("legal.sections.cookies_intro_title"), p: t("legal.sections.cookies_intro") },
        {
          h: t("legal.sections.cookies_list_title"),
          p: `${t("legal.sections.cookies_list_tech")}\n\n${t("legal.sections.cookies_list_analytics")}`,
          table: {
            headers: t("legal.sections.cookies_table_headers"),
            rows: t("legal.sections.cookies_table_rows"),
          },
        },
        { h: t("legal.sections.cookies_withdraw_title"), p: t("legal.sections.cookies_withdraw") },
      ]);
    default:
      return { blocks: [holder] };
  }
}

function kindTitle(t, kind) {
  switch (kind) {
    case "aviso": return t("legal.aviso_title");
    case "privacidad": return t("legal.privacy_title");
    case "envios": return t("legal.shipping_title");
    case "devoluciones": return t("legal.returns_title");
    case "cookies": return t("legal.cookies_title");
    default: return "Legal";
  }
}

export default function LegalPage() {
  const { t, lang } = useLang();
  const { kind } = useParams();
  const title = kindTitle(t, kind);
  const content = buildContent(t, kind);
  const canonical = `${SITE_URL}/legal/${kind}`;

  return (
    <main data-testid={`legal-${kind}`} className="min-h-screen bg-cream-100 text-charcoal">
      <Seo
        title={`${title} | Cortijo Santa María`}
        description={t("legal.sections.holder_body")}
        canonical={canonical}
        lang={lang}
        noindex
        jsonLd={breadcrumbLd([
          { name: t("nav_ecom.home"), url: `${SITE_URL}/` },
          { name: title, url: canonical },
        ])}
      />
      <ShopNav />
      <article className="max-w-3xl mx-auto px-6 lg:px-12 py-16 sm:py-24">
        <span className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium">
          {t("legal.aviso_title")}
        </span>
        <h1 className="mt-6 font-serif text-4xl sm:text-5xl leading-tight text-olive-900">
          {title}
        </h1>
        <p className="mt-3 text-xs uppercase tracking-[0.2em] text-olive-800/50">
          {t("legal.last_update")}: {LAST_UPDATE}
        </p>

        <div className="mt-10 space-y-10">
          {PRE_HARVEST_MODE && (kind === "envios" || kind === "devoluciones") && (
            <aside
              data-testid="legal-preharvest-notice"
              role="note"
              className="flex items-start gap-3 border-l-4 border-amber-500 bg-amber-50 px-5 py-4"
            >
              <AlertTriangle size={18} strokeWidth={1.4} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-900 font-light leading-relaxed">
                {lang === "es"
                  ? "Actualmente no aceptamos pedidos ni realizamos envíos. La próxima cosecha está en preparación. Puedes unirte a la lista de espera desde la página de inicio."
                  : "We are not currently accepting orders or shipping products. The next harvest is in preparation. You can join the waitlist from the home page."}
                {" "}
                <Link to="/" className="underline font-medium hover:text-amber-700">
                  {lang === "es" ? "Ir al inicio →" : "Go to home →"}
                </Link>
              </p>
            </aside>
          )}
          {content.blocks.map((b, i) => (
            <section key={b.h} data-testid={`legal-section-${i}`}>
              <h2 className="font-serif text-2xl text-olive-900">{b.h}</h2>
              <p className="mt-3 text-olive-800/85 font-light leading-relaxed whitespace-pre-line">
                {b.p}
              </p>
              {b.table && (
                <div
                  data-testid="cookies-table"
                  className="mt-6 overflow-x-auto border border-cream-400 bg-cream-100"
                >
                  <table className="w-full min-w-[640px] text-sm text-olive-900/90 font-light">
                    <thead className="bg-cream-200/70 text-[11px] uppercase tracking-[0.2em] text-olive-800/70">
                      <tr>
                        {b.table.headers.map((h) => (
                          <th key={h} scope="col" className="text-left px-4 py-3 font-medium">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {b.table.rows.map((row, ri) => (
                        <tr
                          key={row.name}
                          className={ri < b.table.rows.length - 1 ? "border-b border-cream-400" : ""}
                          data-testid={`cookies-row-${ri}`}
                        >
                          <td className="align-top px-4 py-3 font-mono text-[12px] text-olive-900 whitespace-nowrap">
                            {row.name}
                          </td>
                          <td className="align-top px-4 py-3">{row.type}</td>
                          <td className="align-top px-4 py-3 leading-snug">{row.purpose}</td>
                          <td className="align-top px-4 py-3 whitespace-nowrap">{row.duration}</td>
                          <td className="align-top px-4 py-3 leading-snug">{row.third}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}
        </div>

        <nav className="mt-16 border-t border-cream-400 pt-8 flex flex-wrap gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.24em] text-olive-800/70">
          <Link to="/legal/aviso" className="hover:text-olive-900">{t("legal.aviso_title")}</Link>
          <Link to="/legal/privacidad" className="hover:text-olive-900">{t("legal.privacy_title")}</Link>
          <Link to="/legal/envios" className="hover:text-olive-900">{t("legal.shipping_title")}</Link>
          <Link to="/legal/devoluciones" className="hover:text-olive-900">{t("legal.returns_title")}</Link>
          <Link to="/legal/cookies" className="hover:text-olive-900">{t("legal.cookies_title")}</Link>
        </nav>

        <div className="mt-10">
          <Link to="/" className="text-[11px] uppercase tracking-[0.28em] text-olive-800 hover:text-olive-900">
            ← {t("legal.back_home")}
          </Link>
        </div>
      </article>
      <Footer />
    </main>
  );
}
